import { Component, Input, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';

interface ForecastDataItem {
  day: string;
  dayEpoch: number;
  remainingDays: number;
  pdppAmount: number;
  activePatients: number;
  dailyNeed: number;
  totalOrderAmount: number;
  totalInvoiceAmount: number;
  forecastAmount: number;
  pcChanged: boolean;
  budget: number;
}

interface ForecastData {
  forecastData: ForecastDataItem[];
  totalEPCount: number;
  siteEPCount: number;
  totalBudget: number;
  siteBudget: number;
  savings: number;
  totalAPCount: number;
  totalSiteCount: number;
  usedBudget: number;
  remainingBudget: number;
}

@Component({
  selector: 'app-expected-vs-active-patients',
  standalone: true,
  imports: [CommonModule, ChartModule],
  templateUrl: './expected-vs-active-patients.component.html',
  styleUrl: './expected-vs-active-patients.component.scss'
})
export class ExpectedVsActivePatientsComponent implements OnChanges, OnInit {
  @Input() protocolForecastData: ForecastData | null = null;

  private limitXAxisPoints = false;
  private maxXAxisPoints = 6;
  private customLinePlugin: any;
  private showCurrentDateLine = false;

  data = {
    labels: [],
    datasets: [
      {
        label: 'Expected Patients',
        data: [],
        borderColor: '#9D1E65',
        backgroundColor: 'rgba(110,231,183,0.2)',
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 0,
        pointBackgroundColor: '#9D1E65',
        pointBorderColor: '#fff',
        pointHoverRadius: 8,
        fill: false
      },
      {
        label: 'Active Patients',
        data: [],
        borderColor: '#65C9BB',
        backgroundColor: 'rgba(96,165,250,0.2)',
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 0,
        pointBackgroundColor: '#65C9BB',
        pointBorderColor: '#fff',
        pointHoverRadius: 8,
        fill: false
      }
    ]
  };

  options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'center',
        padding: 30,
        labels: {
          boxWidth: 26,
          boxHeight: 12,
          usePointStyle: true,
          pointStyle: 'rect'
        }
      },
      title: {
        display: false,
        text: ''
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#651244',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#ffffff',
        borderWidth: 1
      },
      expectedVsActivePatientsLinePlugin: {}
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: {
          stepSize: 10
        },
        grid: {
          color: '#e5e7eb'
        }
      },
      x: {
        display: true,
        grid: {
          display: false
        }
      }
    },
    interaction: {
      mode: 'nearest',
      intersect: false
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.protocolForecastData && this.protocolForecastData) {
      this.updateChartData();
    }
  }

  ngOnInit() {
    this.customLinePlugin = {
      id: 'expectedVsActivePatientsLinePlugin',
      afterDraw: (chart: any) => {
        if (!this.showCurrentDateLine) {
          return;
        }

        try {
          const { ctx, chartArea, scales } = chart;

          if (!ctx || !chartArea || !scales || !scales.x) {
            return;
          }

          const labels = chart.data.labels;
          if (!labels || labels.length === 0) {
            return;
          }

          const currentIndex = this.getCurrentDateIndex(labels);
          if (currentIndex === -1 || currentIndex >= labels.length) {
            return;
          }

          const x = scales.x.getPixelForValue(labels[currentIndex]);
          if (isNaN(x) || x < chartArea.left || x > chartArea.right) {
            return;
          }

          const topY = chartArea.top;
          const bottomY = chartArea.bottom;

          ctx.save();
          ctx.beginPath();
          ctx.setLineDash([5, 5]);
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          ctx.strokeStyle = '#222';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.setLineDash([]);

          ctx.beginPath();
          ctx.arc(x, topY, 7, 0, 2 * Math.PI);
          ctx.strokeStyle = '#222';
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.restore();
        } catch (error) {
          // Handle chart drawing error silently
        }
      }
    };
    Chart.register(this.customLinePlugin);
  }

  private updateChartData(): void {
    if (!this.protocolForecastData?.forecastData) return;

    const forecastData = this.protocolForecastData.forecastData;

    let selectedData = forecastData;

    if (this.limitXAxisPoints && forecastData.length > this.maxXAxisPoints) {
      const totalPoints = forecastData.length;
      const selectedIndices = [];
      // Always include the first point
      selectedIndices.push(0);
      // Calculate the number of points to select between start and end
      const pointsToSelect = this.maxXAxisPoints - 2;
      for (let i = 1; i <= pointsToSelect; i++) {
        const index = Math.round(i * (totalPoints - 1) / (this.maxXAxisPoints - 1));
        if (!selectedIndices.includes(index)) {
          selectedIndices.push(index);
        }
      }
      // Always include the last point
      if (!selectedIndices.includes(totalPoints - 1)) {
        selectedIndices.push(totalPoints - 1);
      }
      // Sort indices to maintain order
      selectedIndices.sort((a, b) => a - b);
      selectedData = selectedIndices.map(index => forecastData[index]);
    }

    const labels: string[] = [];
    const expectedPatients: number[] = [];
    const activePatients: number[] = [];

    selectedData.forEach(item => {
      const date = new Date(item.day);
      const formattedDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });

      labels.push(formattedDate);
      const epCount = this.protocolForecastData.totalEPCount || 0;
      expectedPatients.push(Number(epCount.toFixed(2)));
      activePatients.push(item.activePatients);
    });

    this.data = {
      labels,
      datasets: [
        {
          label: 'Expected Patients',
          data: expectedPatients,
          borderColor: '#9D1E65',
          backgroundColor: 'rgba(110,231,183,0.2)',
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 0,
          pointBackgroundColor: '#9D1E65',
          pointBorderColor: '#fff',
          pointHoverRadius: 8,
          fill: false
        },
        {
          label: 'Active Patients',
          data: activePatients,
          borderColor: '#65C9BB',
          backgroundColor: 'rgba(96,165,250,0.2)',
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 0,
          pointBackgroundColor: '#65C9BB',
          pointBorderColor: '#fff',
          pointHoverRadius: 8,
          fill: false
        }
      ]
    };

    const maxValue = Math.max(...expectedPatients, ...activePatients);
    const paddedMax = Math.ceil((maxValue * 1.1) / 10) * 10;
    this.options.scales.y.max = paddedMax;
  }

  getCurrentDateIndex(labels: string[]): number {
    try {
      if (!labels || labels.length === 0) {
        return -1;
      }

      const today = new Date();
      const todayString = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      const exactMatch = labels.findIndex(label => label === todayString);
      if (exactMatch !== -1) {
        return exactMatch;
      }

      let closestIndex = 0;
      let minDifference = Infinity;

      labels.forEach((label, index) => {
        try {
          const labelDate = new Date(`${label} ${today.getFullYear()}`);
          if (!isNaN(labelDate.getTime())) {
            const difference = Math.abs(today.getTime() - labelDate.getTime());
            if (difference < minDifference) {
              minDifference = difference;
              closestIndex = index;
            }
          }
        } catch (error) {
          // Handle date parsing error silently
        }
      });

      return closestIndex;
    } catch (error) {
      return 0;
    }
  }

  setXAxisPointLimit(enabled: boolean, maxPoints: number = 6): void {
    this.limitXAxisPoints = enabled;
    this.maxXAxisPoints = maxPoints;
    this.updateChartData();
  }

  toggleXAxisPointLimit(): void {
    this.limitXAxisPoints = !this.limitXAxisPoints;
    this.updateChartData();
  }

  getXAxisPointLimitStatus(): boolean {
    return this.limitXAxisPoints;
  }

  setCurrentDateLineVisibility(show: boolean): void {
    this.showCurrentDateLine = show;
  }

  toggleCurrentDateLine(): void {
    this.showCurrentDateLine = !this.showCurrentDateLine;
  }

  getCurrentDateLineStatus(): boolean {
    return this.showCurrentDateLine;
  }
}
